import {DeviceManager} from "../managers/DeviceManager";
import {SoundManager} from "../managers/SoundManager";
import {ErrorInfo, ErrorManager} from "../managers/ErrorManager";
import {NetworkManager} from "../managers/NetworkManager";
import {GameDispatcher} from "../pixi";
import {LocaleManager} from "../managers";

export abstract class AbstractApp {

    readonly onRouteChange = new GameDispatcher();

    device!: DeviceManager;
    sound!: SoundManager;
    error!: ErrorManager;
    network!: NetworkManager;
    locale! :LocaleManager;

    private _tasksQueue: (() => void)[] = [];  // Очередь задач
    private _isAppActive = true;  // Флаг активности приложения
    private _isExecuting = false;  // Флаг для контроля выполнения

    private _currentRoute:string | null = null;
    private _currentRouteQuery:Record<string, string> | null = null;
    private _forceChangeRouteCallback: ((route:string, query: Record<string, string>) => void) | null = null;

    get isDevMode():boolean {
        throw new Error('Need to be implemented');
    }

    get currentRoute() {
        return this._currentRoute;
    }

    get currentRouteQuery() {
        return this._currentRouteQuery;
    }

    set forceChangeRouteCallback(value: ((route: string, query: Record<string, string>) => void) | null) {
        this._forceChangeRouteCallback = value;
    }

    onRouteChanged(route:string, query: Record<string, string>){
        if (route !== this._currentRoute) {
            this._currentRoute = route;
            this._currentRouteQuery = query;
            this.onRouteChange.dispatch();
        }
    }

    forceChangeRoute(route:string, query: Record<string, string>) {
        if (this._forceChangeRouteCallback) {
            this._forceChangeRouteCallback(route, query);
        }
    }

    log(...args: any[]) {
        if (this.isDevMode) {
            for (const arg of args) {
                console.log(arg);
            }
            // console.trace('Logged from:');
        }
    }

    logError(...args: any[]) {
        if (this.isDevMode) {
            for (const arg of args) {
                console.error("ERROR LOG: " + arg);
            }
            console.trace('Logged from:');
        }
    }

    async init(managers: {
        locale?:LocaleManager,
        error?: ErrorManager,
        device?: DeviceManager,
        sound?: SoundManager,
        network?: NetworkManager,
    }) {
        this.error = managers.error || new ErrorManager(this);
        this.error.init((errorInfo: ErrorInfo)=>{
            if (errorInfo.isUncaught) {
                this.logError('ERROR:onUncaughtError: ', JSON.stringify(errorInfo));
            } else {
                this.log('ERROR:onCaughtError:', JSON.stringify(errorInfo));
            }
            this.onError(errorInfo);
        });

        this.device = managers.device || new DeviceManager();
        await this.device.init();

        this.sound = managers.sound || new SoundManager(this, true);
        await this.sound.init();

        this.network = managers.network || new NetworkManager();
        await this.network.init();

        this.locale = managers.locale || new LocaleManager(this, true, [LocaleManager.en]);
        await this.locale.init();

        this.device.onActivateEvent.add(this.onActivate, this);
        this.device.onDeactivateEvent.add(this.onDeactivate, this);
    }

    protected onError(errorInfo: ErrorInfo) {
        // todo
        // if (errorInfo.isUncaught) {
        //     // Main.rootNavigator.showDialog(new ErrorDialog(Locale.getText(Locale.app_error), Main.device.forceExit.bind(Main.device)));
        // }
        // errorInfo.version = AppConfig.GAME_VERSION;
        // errorInfo.playerId = Main.user?.userData?.objectId;
        // errorInfo.screenState = Main.rootNavigator?.currentScreen?.getStateDescription();
        // Main.server.request(ServerService.FUNCTION_UNCAUGHT_ERROR, error,
        //     () => {
        //         // success callback
        //     },
        //     (error: ParseRequestError) => {
        //         // error callback
        //     }
        // );
        // // todo: firebase crashlytics
    }

    navigateToURL(url: string) {
        window.open(url, '_system');
    }

    executeWhenApplicationIsActive(callback: Function): void {
        // Если приложение активно
        if (this._isAppActive) {
            this._tasksQueue.push(() => {
                if (callback) {
                    callback();
                }
            });
            // Если в данный момент нет выполняющихся задач, запускаем первую
            if (!this._isExecuting) {
                this.processQueue();
            }
        } else {
            // Если приложение не активно, добавляем задачу в очередь
            this._tasksQueue.push(() => {
                if (callback) {
                    callback();
                }
            });
        }
    }

    private onActivate() {
        this.log('App.onActivate');
        this._isAppActive = true;
        this.processQueue();  // После возобновления нужно продолжить обработку очереди
        void this.sound.onActivate();
    }

    private onDeactivate(){
        this.log('App.onDeactivate');
        this._isAppActive = false;
        void this.sound.onDeActivate();
    }

    private processQueue(): void {
        if (this._tasksQueue.length > 0) {
            this._isExecuting = true;
            // Получаем и выполняем первую задачу в очереди
            const task = this._tasksQueue.shift();
            if (task) {
                requestAnimationFrame(() => {
                    task();
                    this._isExecuting = false;
                    this.processQueue();  // Обрабатываем следующую задачу в очереди
                });
            }
        }
    }

}